<template>
  <nav class="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 sticky top-0 z-40">
    <div class="px-4 sm:px-6">
      <div class="flex items-center justify-between h-14">
        <!-- Logo -->
        <router-link :to="{ name: 'Home' }" class="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
          <img
            src="@/assets/globe.png"
            class="h-8 w-8"
            alt="Neutral Fuels Logo"
          />
          <div class="flex items-baseline gap-1.5">
            <span class="text-base font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Neutral Fuels</span>
            <span class="text-xs font-semibold text-primary-600 dark:text-primary-400">OMS</span>
          </div>
        </router-link>

        <!-- Authenticated -->
        <div v-if="user" class="flex items-center gap-3">
          <!-- Site Selector -->
          <select
            @change="onChange($event)"
            v-model="userSite"
            :disabled="isSwitchingSite"
            aria-label="Switch site"
            class="px-3 py-1.5 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm bg-neutral-50 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400 cursor-pointer disabled:opacity-50 disabled:cursor-wait"
          >
            <option
              :value="site.siteId"
              v-for="site in availableSites"
              :key="site.siteId"
            >
              {{ site.siteName }}
            </option>
          </select>

          <!-- Separator -->
          <div class="h-5 w-px bg-neutral-200 dark:bg-neutral-700"></div>

          <!-- Dark Mode Toggle -->
          <button
            @click="toggleDarkMode"
            class="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
            :title="isDark ? 'Light mode' : 'Dark mode'"
            :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
            :aria-pressed="isDark"
          >
            <span class="material-icons text-lg" aria-hidden="true">
              {{ isDark ? 'light_mode' : 'dark_mode' }}
            </span>
          </button>

          <!-- Notifications -->
          <NotificationBell />

          <!-- User -->
          <div class="flex items-center gap-2">
            <div class="w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <span class="text-xs font-bold text-primary-700 dark:text-primary-300">
                {{ user.displayName?.charAt(0)?.toUpperCase() }}
              </span>
            </div>
            <span class="text-sm font-medium text-neutral-700 dark:text-neutral-300 hidden sm:inline">{{ user.displayName }}</span>
          </div>

          <!-- Version -->
          <span class="text-[10px] text-neutral-500 dark:text-neutral-400 hidden sm:inline">{{ versionNumber }}</span>

          <!-- Logout -->
          <button
            @click="handleClick"
            class="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-neutral-500 dark:text-neutral-400 hover:text-red-600 dark:hover:text-red-400"
            title="Logout"
            aria-label="Log out"
          >
            <span class="material-icons text-lg" aria-hidden="true">logout</span>
          </button>
        </div>

        <!-- Unauthenticated -->
        <div v-if="!user" class="flex items-center gap-3">
          <span class="text-xs text-neutral-500 dark:text-neutral-400">{{ versionNumber }}</span>
          <router-link
            :to="{ name: 'Signup' }"
            class="px-3 py-1.5 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors text-sm font-medium"
          >
            Sign Up
          </router-link>
          <router-link
            :to="{ name: 'Login' }"
            class="px-4 py-1.5 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors text-sm font-semibold"
          >
            Login
          </router-link>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { computed, ref } from "@vue/runtime-core";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/firebase/config";
import useDarkMode from "@/composables/useDarkMode";
import useToast from "@/composables/useToast";
import NotificationBell from "@/components/NotificationBell.vue";

export default {
  components: { NotificationBell },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { isDark, toggleDarkMode } = useDarkMode();
    const { showToast } = useToast();
    const isSwitchingSite = ref(false);

    const handleClick = async () => {
      await store.dispatch("logout");
      router.push({ name: "Login" });
    };

    const onChange = async (event) => {
      isSwitchingSite.value = true;
      try {
        const userRef = doc(db, "users", store.state.user.uid);
        await updateDoc(userRef, {
          defaulltSite: event.target.value,
        });
        store.commit("setCurrentSite", event.target.value);
        // Full reload is intentional: desktop composables read currentSite
        // once on setup, so site-scoped data only refreshes on reload.
        window.location.reload();
      } catch (error) {
        console.error("Error switching site:", error);
        showToast("error", "Failed to switch site. Please try again.");
        // Snap the select back to the site that is actually active.
        event.target.value = store.state.currentSite;
        isSwitchingSite.value = false;
      }
    };

    return {
      user: computed(() => store.state.user),
      versionNumber: computed(() => store.state.versionNumber),
      userSite: computed(() => store.state.currentSite),
      availableSites: computed(() => store.state.userSites),
      handleClick,
      onChange,
      isDark,
      toggleDarkMode,
      isSwitchingSite,
    };
  },
};
</script>
